import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { StepsModule } from 'primeng/steps';
import { MessageService } from 'primeng/api';
import { AbstractView, MenuItemExtended } from '@core/interfaces';
import { StepMenuViewPresenter } from './interfaces';
import { SharedModule } from '@shared/shared.module';
import { ComponentHostDirective } from './directives/component-host.directive';
import { StepMenuService } from './services/step-menu.service';
import { StepComponent } from './interfaces/step-component';
import { StepMenuPresenter } from './step-menu.presenter';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { StepperModule } from 'primeng/stepper';
import { markFormGroupTouched } from '@core/helpers';
import { SplitButtonModule } from 'primeng/splitbutton';
import { Observable, of, take } from 'rxjs';
import { Store } from '@ngrx/store';

@Component({
  selector: 'app-step-menu',
  standalone: true,
  imports: [
    SharedModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    StepsModule,
    ComponentHostDirective,
    StepperModule,
    SplitButtonModule,
  ],
  templateUrl: './step-menu.component.html',
  styleUrl: './step-menu.component.scss',
  providers: [
    StepMenuService,
    StepMenuPresenter,
    StepMenuService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StepMenuComponent),
      multi: true,
    },
  ],
})
export class StepMenuComponent<T = undefined>
  extends AbstractView<StepMenuPresenter<T>>
  implements StepMenuViewPresenter, ControlValueAccessor, OnInit, OnDestroy
{
  activeStep = 0;
  lastStep = 0;
  @Input() isStepper = true;
  @Input({ required: true }) buttomFinishName!: string;
  @Input() buttomFinishIco: string|undefined;
  @Input({ required: true }) routeItems: MenuItemExtended<T>[] = [];
  @Input() stepChange$: Observable<number> = of(0);
  @Output() activeItemChange = new EventEmitter<MenuItemExtended<T>>();
  @Output() cancelChange = new EventEmitter<void>();
  @Output() finishChange = new EventEmitter<void>();
  @Output() instanceCreated = new EventEmitter<T>();

  constructor(
    messageService: MessageService,
    router: Router,
    store: Store,
    presenter: StepMenuPresenter<T>,
    private stepMenuService: StepMenuService,
  ) {
    super(messageService, router, store, presenter);
  }

  ngOnInit(): void {
    this.presenter.setView(this);
  }

  ngOnDestroy(): void {
    this.onStepChange(0);
  }

  // Propiedad enlazada a [(ngModel)]
  private _value: MenuItemExtended<T> | undefined = this.routeItems[0];

  // Callbacks para ControlValueAccessor
  private onChange: (_: MenuItemExtended<T>) => void = () => {};
  private onTouched: () => void = () => {};

  get value(): MenuItemExtended<T> | undefined {
    return this._value;
  }

  set value(value: MenuItemExtended<T>) {
    this._value = value;
    this.onChange(value); // Notificamos cambios
  }

  cancelStep(): void {
    this.cancelChange.emit();
    this.activeStep = 0;
  }

  prevStep(): void {
    if (this.activeStep > 0){
      this.activeStep = this.presenter.findPrevStepAvailable(this.activeStep);
      this.onStepChange(this.activeStep)
    }
  }

  nextStep(): void {
    this.stepMenuService.instances$
    .pipe(
      take(1)
    )
      .subscribe((instances) => {
        const instance = instances[this.activeStep]

        if (instance)
          markFormGroupTouched(instance.formGroup, true);
          if (instance.formGroup.valid){
            this.activeStep = this.presenter.findNextStepAvailable(this.activeStep);
            this.onStepChange(this.activeStep)
          }
      });
  }

  finishStep(): void {
    this.stepMenuService.instances$.
      subscribe((instances) => {
        const instance = instances[this.activeStep]

        if (instance)
          markFormGroupTouched(instance?.formGroup, true);
          if (!!instance?.formGroup?.valid || !instance?.formGroup)
            this.finishChange.emit();
      });
  }

  // Implementación de ControlValueAccessor
  writeValue(value: MenuItemExtended<T>): void {
    this._value = value;
  }

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onComponentInstanceCreated(instance: T): void {
    const component = instance as StepComponent;
    this.stepMenuService.registerForm(component);
  }

  onStepChange(step: number): void {
    this.presenter.onStepChange(step);
  }

  trackByRouteItem(index: number, _item: MenuItemExtended<T>): number {
    return index;
  }
}
